import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TypeService,
  UnitService,
  CostCenterService,
  CategoryService,
  ItemService,
  ShopService,
  VendorService,
  ConfigFlagService,
  ModuleService,
  ExpenseNatureService,
  BusinessType,
  Unit,
  CostCenter,
  ExpenseCategory,
  Item,
  Shop,
  Vendor,
  ConfigFlag,
  Module,
  ExpenseNature
} from '../services/settingsService';

// Query keys - matching your existing Firebase collections
export const QUERY_KEYS = {
  TYPES: ['types'], // Using 'types' not 'businessTypes'
  UNITS: ['units'],
  COST_CENTERS: ['costCenters'],
  CATEGORIES: ['categories'], // Using 'categories' not 'expenseCategories'
  ITEMS: ['items'],
  SHOPS: ['shops'],
  VENDORS: ['vendors'],
  CONFIG_FLAGS: ['configFlags'],
  MODULES: ['modules'],
  EXPENSE_NATURES: ['expenseNatures'],
};

// Types hooks
export const useTypes = () => {
  return useQuery({
    queryKey: QUERY_KEYS.TYPES,
    queryFn: TypeService.getAll,
  });
};

export const useTypeMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<BusinessType, 'id'>) => TypeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TYPES });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BusinessType> }) => 
      TypeService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TYPES });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => TypeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TYPES });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Units hooks
export const useUnits = () => {
  return useQuery({
    queryKey: QUERY_KEYS.UNITS,
    queryFn: UnitService.getAll,
  });
};

export const useUnitMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<Unit, 'id'>) => UnitService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNITS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Unit> }) => 
      UnitService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNITS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => UnitService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.UNITS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Cost Centers hooks
export const useCostCenters = () => {
  return useQuery({
    queryKey: QUERY_KEYS.COST_CENTERS,
    queryFn: CostCenterService.getAll,
  });
};

export const useCostCenterMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<CostCenter, 'id'>) => CostCenterService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COST_CENTERS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CostCenter> }) => 
      CostCenterService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COST_CENTERS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => CostCenterService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COST_CENTERS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Categories hooks
export const useCategories = () => {
  return useQuery({
    queryKey: QUERY_KEYS.CATEGORIES,
    queryFn: CategoryService.getAll,
  });
};

export const useCategoryMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<ExpenseCategory, 'id'>) => CategoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CATEGORIES });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ExpenseCategory> }) => 
      CategoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CATEGORIES });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => CategoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CATEGORIES });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Items hooks
export const useItems = () => {
  return useQuery({
    queryKey: QUERY_KEYS.ITEMS,
    queryFn: ItemService.getAll,
  });
};

export const useItemMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<Item, 'id'>) => ItemService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ITEMS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Item> }) => 
      ItemService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ITEMS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ItemService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ITEMS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Shops hooks
export const useShops = () => {
  return useQuery({
    queryKey: QUERY_KEYS.SHOPS,
    queryFn: ShopService.getAll,
  });
};

export const useShopMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<Shop, 'id'>) => ShopService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SHOPS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Shop> }) => 
      ShopService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SHOPS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ShopService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SHOPS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Vendors hooks
export const useVendors = () => {
  return useQuery({
    queryKey: QUERY_KEYS.VENDORS,
    queryFn: VendorService.getAll,
  });
};

export const useVendorMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<Vendor, 'id'>) => VendorService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.VENDORS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Vendor> }) => 
      VendorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.VENDORS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => VendorService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.VENDORS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Config Flags hooks
export const useConfigFlags = () => {
  return useQuery({
    queryKey: QUERY_KEYS.CONFIG_FLAGS,
    queryFn: ConfigFlagService.getAll,
  });
};

export const useConfigFlagMutation = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data: Omit<ConfigFlag, 'id'>) => ConfigFlagService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONFIG_FLAGS });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ConfigFlag> }) => 
      ConfigFlagService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONFIG_FLAGS });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ConfigFlagService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONFIG_FLAGS });
    },
  });
  
  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};

// Modules hooks
export const useModules = () => {
  return useQuery({
    queryKey: QUERY_KEYS.MODULES,
    queryFn: ModuleService.getAll,
  });
};

export const useModuleMutation = () => {
  const queryClient = useQueryClient();

  return {
    createAsync: useMutation({
      mutationFn: (data: Omit<Module, 'id'>) => ModuleService.create(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MODULES });
      },
    }).mutateAsync,

    updateAsync: useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Module> }) =>
        ModuleService.update(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MODULES });
      },
    }).mutateAsync,

    deleteAsync: useMutation({
      mutationFn: (id: string) => ModuleService.delete(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MODULES });
      },
    }).mutateAsync,

    isPending: false,
  };
};

// Expense Natures hooks
export const useExpenseNatures = () => {
  return useQuery({
    queryKey: QUERY_KEYS.EXPENSE_NATURES,
    queryFn: ExpenseNatureService.getAll,
  });
};

export const useExpenseNatureMutation = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: Omit<ExpenseNature, 'id'>) => ExpenseNatureService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EXPENSE_NATURES });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ExpenseNature> }) =>
      ExpenseNatureService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EXPENSE_NATURES });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ExpenseNatureService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EXPENSE_NATURES });
    },
  });

  return {
    createAsync: createMutation.mutateAsync,
    updateAsync: updateMutation.mutateAsync,
    deleteAsync: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};
